import React, { useState, useEffect, useMemo } from "react";
import hosys from "@/api/api";
import { useVenue } from "@/components/shared/VenueContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertTriangle, Clock, RefreshCw, ChevronDown, ChevronUp, Settings } from "lucide-react";
import { format, startOfMonth, endOfMonth, parseISO } from "date-fns";
import { ca } from "date-fns/locale";
import { StatusBadge, ClassificationBadge } from "@/components/overtime/OvertimeStatusBadge";
import OvertimeValidationModal from "@/components/overtime/OvertimeValidationModal";
import WorkerContractForm from "@/components/overtime/WorkerContractForm";
import OvertimeExport from "@/components/overtime/OvertimeExport";

export default function OvertimeControlTab() {
  const { currentVenue, userRole } = useVenue();
  const [records, setRecords] = useState([]);
  const [members, setMembers] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detecting, setDetecting] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedContract, setSelectedContract] = useState(null);
  const [filterWorker, setFilterWorker] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterClass, setFilterClass] = useState("all");
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), "yyyy-MM"));
  const [tab, setTab] = useState("records");
  const [expandedWorker, setExpandedWorker] = useState(null);

  useEffect(() => { if (currentVenue) loadData(); }, [currentVenue, selectedMonth]);

  const loadData = async () => {
    setLoading(true);
    const start = format(startOfMonth(parseISO(selectedMonth + "-01")), "yyyy-MM-dd");
    const end = format(endOfMonth(parseISO(selectedMonth + "-01")), "yyyy-MM-dd");
    const [recs, mems, conts] = await Promise.all([
      hosys.entities.OvertimeRecord.filter({ venue_id: currentVenue.id }),
      hosys.entities.VenueMember.filter({ venue_id: currentVenue.id, active: true }),
      hosys.entities.WorkerContract.filter({ venue_id: currentVenue.id }),
    ]);
    const filtered = recs.filter(r => r.date >= start && r.date <= end);
    setRecords(filtered);
    setMembers(mems);
    setContracts(conts);
    setLoading(false);
  };

  const runDetection = async () => {
    if (!currentVenue) return;
    setDetecting(true);
    const start = parseISO(selectedMonth + "-01");
    const end = endOfMonth(start);
    let day = new Date(start);
    while (day <= end && day <= new Date()) {
      const dateStr = format(day, "yyyy-MM-dd");
      await hosys.functions.invoke("detectOvertime", { venue_id: currentVenue.id, date: dateStr });
      day.setDate(day.getDate() + 1);
    }
    setDetecting(false);
    loadData();
  };

  const getContractForMember = (email) => contracts.find(c => c.user_email === email);

  const formatMins = (mins) => {
    if (!mins && mins !== 0) return "—";
    const h = Math.floor(Math.abs(mins) / 60);
    const m = Math.abs(mins) % 60;
    const sign = mins < 0 ? "-" : "+";
    return `${h > 0 ? sign : ""}${h}h ${m.toString().padStart(2, "0")}m`;
  };

  const filteredRecords = useMemo(() => records.filter(r => {
    if (filterWorker !== "all" && r.user_email !== filterWorker) return false;
    if (filterStatus !== "all" && r.status !== filterStatus) return false;
    if (filterClass !== "all" && r.classification !== filterClass) return false;
    return true;
  }), [records, filterWorker, filterStatus, filterClass]);

  const workerSummary = useMemo(() => members.map(m => {
    const workerRecs = records.filter(r => r.user_email === m.user_email);
    const totalDiff = workerRecs.reduce((s, r) => s + (r.time_difference || 0), 0);
    const overtimeDays = workerRecs.filter(r => r.overtime_detected).length;
    const incidentDays = workerRecs.filter(r => (r.attendance_incidents || []).length > 0).length;
    const pendingCount = workerRecs.filter(r => ["detected", "pending_review"].includes(r.status) && r.time_difference > 0).length;
    const contract = getContractForMember(m.user_email);
    return { ...m, totalDiff, overtimeDays, incidentDays, pendingCount, contract, workerRecs };
  }), [members, records, contracts]);

  const statsTotal = useMemo(() => ({
    pending: records.filter(r => ["detected","pending_review"].includes(r.status) && r.time_difference > 0).length,
    overtime: records.filter(r => r.overtime_detected).length,
    incidents: records.filter(r => (r.attendance_incidents || []).length > 0).length,
    noContract: members.filter(m => !getContractForMember(m.user_email)).length,
  }), [records, members, contracts]);

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <p className="text-sm text-gray-500">Detecció automàtica de possibles hores extraordinàries</p>
        <div className="flex gap-2 flex-wrap">
          <Input type="month" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} className="w-36 text-sm" />
          <Button onClick={runDetection} disabled={detecting} className="bg-[#7B2D3F] hover:bg-[#6a2636] text-white gap-1.5 text-sm">
            <RefreshCw className={`w-4 h-4 ${detecting ? "animate-spin" : ""}`} />
            {detecting ? "Analitzant..." : "Analitzar mes"}
          </Button>
          <OvertimeExport venueId={currentVenue?.id} records={filteredRecords} month={selectedMonth} />
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="border-0 shadow-sm"><CardContent className="p-3"><p className="text-xs text-gray-500">Pendents validació</p><p className="text-2xl font-bold text-yellow-600">{statsTotal.pending}</p></CardContent></Card>
        <Card className="border-0 shadow-sm"><CardContent className="p-3"><p className="text-xs text-gray-500">Dies amb excés</p><p className="text-2xl font-bold text-red-600">{statsTotal.overtime}</p></CardContent></Card>
        <Card className="border-0 shadow-sm"><CardContent className="p-3"><p className="text-xs text-gray-500">Incidències fitxatge</p><p className="text-2xl font-bold text-orange-600">{statsTotal.incidents}</p></CardContent></Card>
        <Card className="border-0 shadow-sm"><CardContent className="p-3"><p className="text-xs text-gray-500">Sense contracte</p><p className="text-2xl font-bold text-gray-600">{statsTotal.noContract}</p></CardContent></Card>
      </div>

      {statsTotal.noContract > 0 && (
        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-xl flex items-start gap-2 text-sm">
          <AlertTriangle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
          <span className="text-yellow-800"><strong>{statsTotal.noContract} treballadors</strong> no tenen contracte configurat. La detecció no serà precisa fins que no s'afegeixi la jornada prevista.</span>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-gray-100 p-1 rounded-xl">
          <TabsTrigger value="records" className="text-sm rounded-lg">Registres detall</TabsTrigger>
          <TabsTrigger value="summary" className="text-sm rounded-lg">Resum per treballador</TabsTrigger>
          <TabsTrigger value="contracts" className="text-sm rounded-lg">Contractes</TabsTrigger>
        </TabsList>

        <TabsContent value="records" className="mt-4 space-y-3">
          <div className="flex flex-wrap gap-2">
            <Select value={filterWorker} onValueChange={setFilterWorker}>
              <SelectTrigger className="w-44 text-sm h-8"><SelectValue placeholder="Treballador" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tots els treballadors</SelectItem>
                {members.map(m => <SelectItem key={m.user_email} value={m.user_email}>{m.user_name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterClass} onValueChange={setFilterClass}>
              <SelectTrigger className="w-44 text-sm h-8"><SelectValue placeholder="Classificació" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Totes</SelectItem>
                <SelectItem value="possible_extraordinaria">Possible extra</SelectItem>
                <SelectItem value="possible_complementaria">Possible compl.</SelectItem>
                <SelectItem value="incidencia_fitxatge">Incidència fitxatge</SelectItem>
                <SelectItem value="pendent_validacio">Pendent validació</SelectItem>
                <SelectItem value="ordinari">Ordinari</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-44 text-sm h-8"><SelectValue placeholder="Estat" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tots els estats</SelectItem>
                <SelectItem value="detected">Detectat</SelectItem>
                <SelectItem value="pending_review">Pendent revisió</SelectItem>
                <SelectItem value="approved_as_overtime">Aprovat extra</SelectItem>
                <SelectItem value="rejected">Rebutjat</SelectItem>
                <SelectItem value="compensated_with_rest">Compensat descans</SelectItem>
                <SelectItem value="paid">Pagat</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {loading ? (
            <div className="text-center py-10 text-gray-400 text-sm">Carregant registres...</div>
          ) : filteredRecords.filter(r => r.classification !== "ordinari" || r.attendance_incidents?.length > 0).length === 0 ? (
            <div className="text-center py-10 text-gray-400 text-sm">
              <Clock className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              <p>No hi ha registres per al període seleccionat</p>
              <p className="text-xs mt-1">Fes clic a "Analitzar mes" per detectar hores</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredRecords
                .filter(r => r.classification !== "ordinari" || r.attendance_incidents?.length > 0)
                .sort((a, b) => b.date.localeCompare(a.date))
                .map(r => (
                  <div key={r.id} className="bg-white rounded-xl border border-gray-100 p-3 flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm text-gray-900">{r.user_name}</span>
                        <span className="text-xs text-gray-400">{format(parseISO(r.date), "EEE d MMM", { locale: ca })}</span>
                        <ClassificationBadge classification={r.classification} />
                        <StatusBadge status={r.status} />
                      </div>
                      <div className="flex gap-4 mt-1 text-xs text-gray-500">
                        <span>Prevista: <strong>{Math.floor(r.planned_work_time/60)}h{r.planned_work_time%60>0?`${r.planned_work_time%60}m`:""}</strong></span>
                        <span>Treballada: <strong>{Math.floor(r.worked_time/60)}h{r.worked_time%60>0?`${r.worked_time%60}m`:""}</strong></span>
                        <span className={r.time_difference > 0 ? "text-red-600 font-semibold" : "text-green-600"}>{formatMins(r.time_difference)}</span>
                      </div>
                      {r.attendance_incidents?.length > 0 && <p className="text-xs text-orange-600 mt-0.5">⚠ {[].concat(r.attendance_incidents).join(" · ")}</p>}
                    </div>
                    {["admin","supervisor","superadmin"].includes(userRole) && (
                      <Button size="sm" variant="outline" onClick={() => setSelectedRecord(r)} className="text-xs flex-shrink-0">Validar</Button>
                    )}
                  </div>
                ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="summary" className="mt-4 space-y-3">
          {workerSummary.map(w => (
            <div key={w.user_email} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="p-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50" onClick={() => setExpandedWorker(expandedWorker === w.user_email ? null : w.user_email)}>
                <div className="w-9 h-9 rounded-xl bg-[#7B2D3F]/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#7B2D3F] font-bold text-sm">{w.user_name?.charAt(0)}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-gray-900">{w.user_name}</p>
                  <p className="text-xs text-gray-400">{w.workerRecs.length} jornades · {w.contract ? `${w.contract.daily_hours}h/dia` : "Sense contracte"}</p>
                </div>
                <div className="flex gap-3 text-center flex-wrap justify-end">
                  {w.pendingCount > 0 && <Badge className="bg-yellow-100 text-yellow-800 text-xs">{w.pendingCount} pendents</Badge>}
                  {w.overtimeDays > 0 && <Badge className="bg-red-100 text-red-800 text-xs">{w.overtimeDays} dies excés</Badge>}
                  {w.incidentDays > 0 && <Badge className="bg-orange-100 text-orange-800 text-xs">{w.incidentDays} incidències</Badge>}
                  <span className={`text-sm font-bold ${w.totalDiff > 0 ? "text-red-600" : "text-green-600"}`}>{formatMins(w.totalDiff)}</span>
                </div>
                {expandedWorker === w.user_email ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
              </div>
              {expandedWorker === w.user_email && (
                <div className="border-t border-gray-100 p-3 space-y-1.5">
                  {w.workerRecs.filter(r => r.classification !== "ordinari" || r.attendance_incidents?.length > 0).map(r => (
                    <div key={r.id} className="flex items-center gap-2 text-xs p-2 bg-gray-50 rounded-lg">
                      <span className="text-gray-500 w-20 flex-shrink-0">{format(parseISO(r.date), "EEE d MMM", { locale: ca })}</span>
                      <ClassificationBadge classification={r.classification} />
                      <span className={`font-medium ${r.time_difference > 0 ? "text-red-600" : "text-green-600"}`}>{formatMins(r.time_difference)}</span>
                      <StatusBadge status={r.status} />
                      {r.attendance_incidents?.length > 0 && <span className="text-orange-600">⚠ {[].concat(r.attendance_incidents)[0]}</span>}
                      {["admin","supervisor","superadmin"].includes(userRole) && (
                        <Button size="sm" variant="ghost" className="h-6 text-xs ml-auto px-2" onClick={() => setSelectedRecord(r)}>Validar</Button>
                      )}
                    </div>
                  ))}
                  {w.workerRecs.filter(r => r.classification !== "ordinari" || r.attendance_incidents?.length > 0).length === 0 && (
                    <p className="text-xs text-gray-400 text-center py-2">Tot correcte aquest mes ✓</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </TabsContent>

        <TabsContent value="contracts" className="mt-4 space-y-3">
          <p className="text-sm text-gray-500">{contracts.length} contractes configurats de {members.length} treballadors</p>
          <div className="space-y-2">
            {members.map(m => {
              const c = getContractForMember(m.user_email);
              return (
                <div key={m.user_email} className="bg-white rounded-xl border border-gray-100 p-3 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-[#7B2D3F]/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-[#7B2D3F] font-bold text-sm">{m.user_name?.charAt(0)}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-gray-900">{m.user_name}</p>
                    {c ? (
                      <p className="text-xs text-gray-500">{c.workday_type === "full_time" ? "Temps complet" : "Temps parcial"} · {c.daily_hours}h/dia · {c.weekly_hours}h/setmana · {c.collective_agreement || "Sense conveni"}</p>
                    ) : (
                      <p className="text-xs text-orange-500">⚠ Sense contracte configurat</p>
                    )}
                  </div>
                  <Button size="sm" variant="outline" onClick={() => { setSelectedMember(m); setSelectedContract(c || null); }} className="text-xs gap-1">
                    <Settings className="w-3 h-3" />{c ? "Editar" : "Configurar"}
                  </Button>
                </div>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {selectedRecord && (
        <OvertimeValidationModal record={selectedRecord} onSave={() => { setSelectedRecord(null); loadData(); }} onClose={() => setSelectedRecord(null)} />
      )}
      {selectedMember && (
        <WorkerContractForm
          venueId={currentVenue.id}
          member={selectedMember}
          contract={selectedContract}
          onSave={() => { setSelectedMember(null); setSelectedContract(null); loadData(); }}
          onClose={() => { setSelectedMember(null); setSelectedContract(null); }}
        />
      )}
    </div>
  );
}